import React, { useState } from "react";
import { Lock } from "lucide-react";
import { queue } from "./techData";
import { Screen, Card, Big } from "./ui";
import { money } from "./LabourCard";

const d10 = (x) => (x || "").slice(0, 10).split("-").reverse().join("/");
// only an Invoice or a Client Counter Sale locks a work order - never its status
const locked = (w) => !!(w.counter_sale || w.status === "counter_sale" || w.locked || w.invoice_id || w.invoice_number);
const STATUSES = [["wo_quotation", "Work Order Quotation"], ["booked", "Work Order Booked"], ["in_progress", "In Progress"], ["completed", "Work Order Completed"]];
export const stLabel = (s) => ({ ...Object.fromEntries(STATUSES), invoiced: "Invoiced", paid: "Paid", counter_sale: "Client Counter Sale", cancelled: "Cancelled" })[s] || s || "";

function StatusPick({ w, onPick }) {
  const [st, setSt] = useState(w.status || "");
  const pick = (v) => { setSt(v); w.status = v; onPick(v); queue(`wostatus:${w.id}`, "PATCH", `/tech/workorder/${w.id}`, { status: v }); };
  return (
    <label className="block" data-testid="tech-wo-view-status-wrap"><span className="text-[12px] font-black text-slate-500">STATUS</span>
      <select value={st} onChange={(e) => pick(e.target.value)} data-testid="tech-wo-view-status" className="w-full h-12 rounded-xl border-2 border-slate-200 px-2 text-[16px] font-black bg-white text-[#0f2f52]">
        {!STATUSES.some(([k]) => k === st) && <option value={st}>{st || "—"}</option>}
        {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <span className="text-[12px] text-slate-500">Set it to Booked or Quotation to add parts &amp; labour from the job screen.</span>
    </label>
  );
}

function EditBox({ w, field, label, bg, testid }) {
  const [t, setT] = useState(w[field] || "");
  const save = () => { if (t === (w[field] || "")) return; w[field] = t; queue(`wo:${w.id}:${field}`, "PATCH", `/tech/workorder/${w.id}`, { [field]: t }); };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-300">
      <div className="px-3 py-1 text-[12px] font-black text-slate-600 bg-white/70">{label}</div>
      <textarea value={t} onChange={(e) => setT(e.target.value)} onBlur={save} rows={4} data-testid={testid} className="w-full p-3 text-[17px] leading-snug outline-none" style={{ background: bg }} />
    </div>
  );
}

function AddNote({ w, field, label, bg, testid }) {
  const [txt, setTxt] = useState(w[field] || ""), [add, setAdd] = useState("");
  const save = () => {
    const t = add.trim();
    if (!t) return;
    const next = txt ? `${txt}\n${t}` : t;
    queue(`wonote:${w.id}:${field}`, "PATCH", `/tech/workorder/${w.id}`, { [field]: next });
    w[field] = next; setTxt(next); setAdd("");
  };
  return (
    <div className="rounded-xl overflow-hidden border border-slate-300" data-testid={testid}>
      <div className="px-3 py-1 text-[12px] font-black text-slate-600 bg-white/70">{label}</div>
      <div className="p-3 text-[16px] whitespace-pre-wrap min-h-[48px]" style={{ background: bg }}>{txt || <span className="text-slate-400">Nothing written</span>}</div>
      <div className="flex gap-2 p-2 bg-white">
        <input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Add a note…" data-testid={`${testid}-input`} className="flex-1 min-w-0 h-12 rounded-lg border-2 border-slate-200 px-2 text-[16px]" />
        <button onClick={save} data-testid={`${testid}-add`} className="h-12 px-4 rounded-lg bg-[#0f2f52] text-white font-black">Add</button>
      </div>
    </div>
  );
}

const ISSUE = ["issue_text", "ISSUE — on Work Order, FM84 & Quote", "#ffc5c5", "issue"];
const RESULT = ["result_text", "RESULT — on Work Order & Invoice", "#ccffcc", "result"];

// work order from Client History: invoiced / counter sale = notes only; anything else stays editable
export default function WoView({ wo: w, name, nav }) {
  const lock = locked(w);
  const [st, setSt] = useState(w.status);
  const lines = w.lines?.length ? w.lines : [...(w.parts || []).map((p) => ({ kind: "part", item: p.name, unit: p.unit_price, qty: p.qty, net: p.qty * p.unit_price })), ...(w.labour || []).map((l) => ({ kind: "labour", item: l.description, unit: l.rate, qty: l.hours, net: l.hours * l.rate }))];
  const old = String(w.id || "").startsWith("legacy-"); // old Spa Buddy work order: read only
  const box = ([field, label, bg, key]) => (old
    ? <Card key={key} className="space-y-1" style={{ background: bg }} data-testid={`tech-wo-view-${key}`}><div className="text-[12px] font-black text-slate-600">{label}</div><div className="text-[16px] whitespace-pre-wrap">{w[field] || "—"}</div></Card>
    : lock
    ? <AddNote key={key} w={w} field={field} label={label} bg={bg} testid={`tech-wo-view-${key}`} />
    : <EditBox key={key} w={w} field={field} label={label} bg={bg} testid={`tech-wo-view-${key}-edit`} />);
  return (
    <Screen title={`W/O ${w.wo_number || "—"}`} sub={name} onBack={nav.back} testid="tech-wo-view" footer={<Big tone="navy" testid="tech-wo-view-back" onClick={nav.back}>Back to history</Big>}>
      <Card className="space-y-1">
        <div className="text-[16px] font-black text-[#0f2f52]">{w.work_type || w.type || ""} · <span data-testid="tech-wo-view-status-label">{stLabel(st)}</span></div>
        <div className="text-[14px] text-slate-600">Booked {d10(w.date_booked) || "—"} · For {d10(w.scheduled_date) || "—"} · Done {d10(w.date_completed) || "—"}{w.technician ? ` · ${w.technician}` : ""}</div>
        {lock && <div className="text-[13px] font-black text-slate-500 flex items-center gap-1" data-testid="tech-wo-view-locked"><Lock className="h-4 w-4" />{w.counter_sale || w.status === "counter_sale" ? "Client Counter Sale" : "Invoiced"} — locked{old ? " (read only)" : ", you can only add notes"}</div>}
        {!lock && <StatusPick w={w} onPick={setSt} />}
      </Card>
      {w.description && !w.issue_text && <Card className="text-[16px]">{w.description}</Card>}
      {box(ISSUE)}
      {box(RESULT)}
      <Card className="space-y-1" data-testid="tech-wo-view-lines">
        <div className="text-[13px] font-black text-slate-500">ITEMS</div>
        {!lines.length && <div className="text-slate-400">No items.</div>}
        {lines.map((l, i) => <div key={i} className="flex justify-between gap-2 text-[15px]"><span>{Number(l.qty) || 0} × {l.item}</span><span className="text-slate-500 whitespace-nowrap">{money(l.net)}</span></div>)}
        <div className="flex justify-between font-black text-[#0f2f52] border-t pt-1"><span>Total</span><span>{money(w.total ?? lines.reduce((a, l) => a + (l.net || 0), 0))}</span></div>
      </Card>
      {w.notes && <Card className="text-[14px] text-amber-900 bg-amber-50 whitespace-pre-wrap">{w.notes}</Card>}
    </Screen>
  );
}
